// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

import {
    useEffect,
    useState,
} from "react";

import {
    financeText,
    getFinanceLanguage,
} from "finance-language";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Custom styles for the SidenavCollapse
import {
    collapseItem,
    collapseIconBox,
    collapseIcon,
    collapseText,
    collapseArrow,
} from "examples/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";

function SidenavCollapse({
                             icon,
                             name,
                             children = false,
                             active = false,
                             noCollapse = false,
                             open = false,
                             openUp = false,
                             ...rest
                         }) {
    const [controller] =
        useMaterialUIController();

    const {
        miniSidenav,
        transparentSidenav,
        whiteSidenav,
        darkMode,
    } = controller;

    const [
        financeLanguage,
        setFinanceLanguage,
    ] = useState(
        getFinanceLanguage()
    );

    useEffect(() => {
        const handleLanguageChange = (
            event
        ) => {
            setFinanceLanguage(
                event?.detail === "tr"
                    ? "tr"
                    : "en"
            );
        };

        window.addEventListener(
            "finance-language-change",
            handleLanguageChange
        );

        return () => {
            window.removeEventListener(
                "finance-language-change",
                handleLanguageChange
            );
        };
    }, []);

    return (
        <>
            {children && openUp && (
                <Collapse
                    in={open}
                    timeout={250}
                    unmountOnExit
                >
                    {children}
                </Collapse>
            )}

            <ListItem
                component="li"
                sx={{
                    width: "100%",
                    px: 1,
                }}
            >
                <MDBox
                    {...rest}
                    sx={(theme) => ({
                        ...collapseItem(
                            theme,
                            {
                                active,
                                transparentSidenav,
                                whiteSidenav,
                                darkMode,
                            }
                        ),

                        width: "100%",
                        minHeight: 44,
                        borderRadius:
                            "12px",
                    })}
                >
                    <ListItemIcon
                        sx={(theme) =>
                            collapseIconBox(
                                theme,
                                {
                                    transparentSidenav,
                                    whiteSidenav,
                                    darkMode,
                                }
                            )
                        }
                    >
                        {typeof icon ===
                        "string" ? (
                            <Icon
                                sx={(
                                    theme
                                ) =>
                                    collapseIcon(
                                        theme,
                                        {
                                            active,
                                        }
                                    )
                                }
                            >
                                {icon}
                            </Icon>
                        ) : (
                            icon
                        )}
                    </ListItemIcon>

                    <ListItemText
                        primary={financeText(
                            name,
                            financeLanguage
                        )}
                        sx={(theme) =>
                            collapseText(
                                theme,
                                {
                                    miniSidenav,
                                    transparentSidenav,
                                    whiteSidenav,
                                    active,
                                }
                            )
                        }
                    />

                    {!noCollapse && (
                        <Icon
                            sx={(theme) =>
                                collapseArrow(
                                    theme,
                                    {
                                        noCollapse,
                                        transparentSidenav,
                                        whiteSidenav,
                                        miniSidenav,
                                        open,
                                        active,
                                        darkMode,
                                    }
                                )
                            }
                        >
                            expand_less
                        </Icon>
                    )}
                </MDBox>
            </ListItem>

            {children && !openUp && (
                <Collapse
                    in={open}
                    timeout={250}
                    unmountOnExit
                >
                    {children}
                </Collapse>
            )}
        </>
    );
}

SidenavCollapse.propTypes = {
    icon: PropTypes.node.isRequired,
    name: PropTypes.string.isRequired,
    children: PropTypes.node,
    active: PropTypes.bool,
    noCollapse: PropTypes.bool,
    open: PropTypes.bool,
    openUp: PropTypes.bool,
};

export default SidenavCollapse;